import React, { useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import { useNavigate, useParams } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faSave,
  faArrowLeft,
  faTimes,
  faEye,
  faDownload,
  faUpload,
  faFilePdf,
  faInfoCircle,
  faFileImage,
  faImages,
} from "@fortawesome/free-solid-svg-icons";
import {
  getStructureAdminById,
  createStructureAdmin,
  updateStructureAdmin,
  getStructureAdministrative,
} from "../../services/api";
import { StructureAdministrative } from "../../types/types";
import { toast } from "react-toastify";
import AdminLayout from "./AdminLayout";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import { useLanguage } from "../../context/LanguageContext";
import { getTranslation } from "../../utils/translations";
import { getFileName, resolveMediaUrl } from "../../utils/media";

const quillModules = {
  toolbar: [
    [{ header: [1, 2, 3, false] }],
    ["bold", "italic", "underline", "strike"],
    [{ list: "ordered" }, { list: "bullet" }],
    [{ align: [] }],
    ["link"],
    ["clean"],
  ],
};

const StructureAdminForm: React.FC = () => {
  const { language: currentLang } = useLanguage();
  const { id } = useParams<{ id?: string }>();
  const isEditMode = Boolean(id);
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [hasExistingRecord, setHasExistingRecord] = useState(false);
  const [existingRecordId, setExistingRecordId] = useState<number | null>(null);

  // File states
  const [arabicFile, setArabicFile] = useState<File | null>(null);
  const [frenchFile, setFrenchFile] = useState<File | null>(null);
  const [arabicFilePreview, setArabicFilePreview] = useState<string | null>(
    null,
  );
  const [frenchFilePreview, setFrenchFilePreview] = useState<string | null>(
    null,
  );

  // Existing file states (for edit mode)
  const [existingArabicFile, setExistingArabicFile] = useState<string>("");
  const [existingFrenchFile, setExistingFrenchFile] = useState<string>("");

  // Text states
  const [arabicText, setArabicText] = useState("");
  const [frenchText, setFrenchText] = useState("");

  // Drag states
  const [arabicDragOver, setArabicDragOver] = useState(false);
  const [frenchDragOver, setFrenchDragOver] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm<StructureAdministrative>();

  // Detect file type from filename
  const detectFileType = (
    fileValue: string,
  ): "pdf" | "image" | "unknown" => {
    if (!fileValue) return "unknown";
    const normalized = fileValue.toLowerCase();
    if (normalized.endsWith(".pdf")) return "pdf";
    if (/\.(jpg|jpeg|png|gif|webp|svg)$/.test(normalized)) return "image";
    return "unknown";
  };

  useEffect(() => {
    // Check for existing records
    const checkExistingRecord = async () => {
      try {
        const response = await getStructureAdministrative();
        if (response.data.length > 0) {
          setHasExistingRecord(true);
          setExistingRecordId(response.data[0].id || null);

          if (!isEditMode) {
            toast.info("يوجد هيكل إداري بالفعل. سيتم نقلك إلى صفحة التعديل.");
            navigate(`/admin/structure-admin/edit/${response.data[0].id}`);
          }
        }
      } catch (error) {
        console.error("Error checking existing records:", error);
      }
    };

    checkExistingRecord();

    // Fetch data for edit mode
    if (isEditMode) {
      const fetchStructure = async () => {
        try {
          setIsLoading(true);
          const response = await getStructureAdminById(parseInt(id!));
          const structure = response.data;

          if (structure) {
            // Reset form with all fields including the new ones
            reset({
              titrearabe: structure.titrearabe || "",
              titrefrancais: structure.titrefrancais || "",
              textarabe: structure.textarabe || "", // (nouveau)
              textfrancais: structure.textfrancais || "", // (nouveau)
            });

            // Set text editor values
            setArabicText(structure.textarabe || "");
            setFrenchText(structure.textfrancais || "");

            // Store existing files
            if (structure.fichierarabe) {
              setExistingArabicFile(structure.fichierarabe);
              const fileType = detectFileType(structure.fichierarabe);
              if (fileType === "image") {
                setArabicFilePreview(resolveMediaUrl(structure.fichierarabe));
              }
            }

            if (structure.fichierfrancais) {
              setExistingFrenchFile(structure.fichierfrancais);
              const fileType = detectFileType(structure.fichierfrancais);
              if (fileType === "image") {
                setFrenchFilePreview(resolveMediaUrl(structure.fichierfrancais));
              }
            }
          }
        } catch (error) {
          console.error("Error fetching structure:", error);
          toast.error("حدث خطأ أثناء جلب بيانات الهيكل الإداري");
        } finally {
          setIsLoading(false);
        }
      };
      fetchStructure();
    }
  }, [id, isEditMode, reset, navigate]);

  const handleFileSelect = (
    e: React.ChangeEvent<HTMLInputElement>,
    language: "arabic" | "french",
  ) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      const fileType = file.type;

      // Accept PDF and image files
      if (fileType.includes("pdf") || fileType.includes("image")) {
        if (language === "arabic") {
          setArabicFile(file);
          // Create preview for images
          if (fileType.includes("image")) {
            const previewUrl = URL.createObjectURL(file);
            setArabicFilePreview(previewUrl);
          } else {
            setArabicFilePreview(null);
          }
        } else {
          setFrenchFile(file);
          // Create preview for images
          if (fileType.includes("image")) {
            const previewUrl = URL.createObjectURL(file);
            setFrenchFilePreview(previewUrl);
          } else {
            setFrenchFilePreview(null);
          }
        }
      } else {
        toast.error("يرجى اختيار ملف PDF أو صورة فقط (PDF, JPG, PNG, GIF)");
      }
    }
  };

  const handleFileDrop = (
    event: React.DragEvent<HTMLDivElement>,
    language: "arabic" | "french",
  ) => {
    event.preventDefault();
    const files = event.dataTransfer.files;
    if (files && files[0]) {
      const file = files[0];
      const fileType = file.type;

      if (fileType.includes("pdf") || fileType.includes("image")) {
        if (language === "arabic") {
          setArabicFile(file);
          if (fileType.includes("image")) {
            const previewUrl = URL.createObjectURL(file);
            setArabicFilePreview(previewUrl);
          } else {
            setArabicFilePreview(null);
          }
          setArabicDragOver(false);
        } else {
          setFrenchFile(file);
          if (fileType.includes("image")) {
            const previewUrl = URL.createObjectURL(file);
            setFrenchFilePreview(previewUrl);
          } else {
            setFrenchFilePreview(null);
          }
          setFrenchDragOver(false);
        }
      } else {
        toast.error("يرجى سحب ملف PDF أو صورة فقط (PDF, JPG, PNG, GIF)");
      }
    }
  };

  const removeFile = (language: "arabic" | "french") => {
    if (language === "arabic") {
      if (arabicFilePreview) {
        URL.revokeObjectURL(arabicFilePreview);
      }
      setArabicFile(null);
      setArabicFilePreview(null);
      setExistingArabicFile("");
    } else {
      if (frenchFilePreview) {
        URL.revokeObjectURL(frenchFilePreview);
      }
      setFrenchFile(null);
      setFrenchFilePreview(null);
      setExistingFrenchFile("");
    }
  };

  const viewExistingFile = (
    fileValue: string,
    language: "arabic" | "french",
  ) => {
    if (!fileValue) return;
    window.open(resolveMediaUrl(fileValue), "_blank");
  };

  const downloadExistingFile = (
    fileValue: string,
    filename: string,
    language: "arabic" | "french",
  ) => {
    if (!fileValue) return;

    const link = document.createElement("a");
    link.href = resolveMediaUrl(fileValue);
    link.download = getFileName(fileValue) || filename;
    link.click();
  };

  const encodeFileContent = (file: File): Promise<string> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => {
        const encodedContent = reader.result as string;
        resolve(encodedContent);
      };
      reader.onerror = (error) => reject(error);
    });
  };

  const onSubmit = async (data: StructureAdministrative) => {
    setIsLoading(true);
    try {
      let fichierarabe = "";
      let fichierfrancais = "";

      // Process Arabic file
      if (arabicFile) {
        fichierarabe = await encodeFileContent(arabicFile);
      } else if (existingArabicFile && isEditMode) {
        fichierarabe = getFileName(existingArabicFile) || existingArabicFile;
      }

      // Process French file
      if (frenchFile) {
        fichierfrancais = await encodeFileContent(frenchFile);
      } else if (existingFrenchFile && isEditMode) {
        fichierfrancais = getFileName(existingFrenchFile) || existingFrenchFile;
      }

      // Prepare payload EXACTLY as your API expects
      const payload = {
        titrearabe: data.titrearabe,
        titrefrancais: data.titrefrancais,
        fichierarabe: fichierarabe,
        fichierfrancais: fichierfrancais,
        textarabe: arabicText, // (nouveau)
        textfrancais: frenchText, // (nouveau)
      };

      if (isEditMode) {
        await updateStructureAdmin(parseInt(id!), payload);
        toast.success("تم تحديث الهيكل الإداري بنجاح");
      } else {
        await createStructureAdmin(payload);
        toast.success("تمت إضافة الهيكل الإداري بنجاح");
      }

      navigate("/admin/structure-admin");
    } catch (error: any) {
      console.error("Error saving structure:", error);
      const errorMessage =
        error.response?.data?.message ||
        getTranslation("saveError", currentLang);
      toast.error(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  // Clean up object URLs
  useEffect(() => {
    return () => {
      if (arabicFilePreview) {
        URL.revokeObjectURL(arabicFilePreview);
      }
      if (frenchFilePreview) {
        URL.revokeObjectURL(frenchFilePreview);
      }
    };
  }, [arabicFilePreview, frenchFilePreview]);

  // Render file upload section
  const renderFileUpload = (
    language: "arabic" | "french",
    file: File | null,
    preview: string | null,
    existingFileValue: string,
    dragOver: boolean,
    setDragOver: (value: boolean) => void,
  ) => {
    const label = language === "arabic" ? "الملف العربي" : "الملف الفرنسي";
    const fileName = language === "arabic" ? "ملف_عربي" : "ملف_فرنسي";
    const fileType = detectFileType(existingFileValue);
    const hasExistingFile = existingFileValue && isEditMode;

    return (
      <div>
        <label className="block text-gray-700 mb-2">
          {label} (PDF أو صورة) *
        </label>
        <div
          className={`border-2 border-dashed rounded-lg p-6 text-center cursor-pointer transition-colors ${
            dragOver
              ? "border-mauritania-green bg-mauritania-green-light"
              : "border-gray-300 hover:border-mauritania-green"
          }`}
          onDrop={(e) => handleFileDrop(e, language)}
          onDragOver={(e) => {
            e.preventDefault();
            setDragOver(true);
          }}
          onDragLeave={() => setDragOver(false)}
          onClick={() =>
            document.getElementById(`${language}-file-input`)?.click()
          }
        >
          <input
            id={`${language}-file-input`}
            type="file"
            accept=".pdf,image/*"
            onChange={(e) => handleFileSelect(e, language)}
            className="hidden"
          />

          {file ? (
            <div className="flex flex-col items-center">
              <div className="relative">
                {preview ? (
                  <>
                    <img
                      src={preview}
                      alt="Preview"
                      className="h-32 w-32 object-cover rounded-md mb-2 border"
                    />
                    <FontAwesomeIcon
                      icon={faFileImage}
                      className="absolute top-0 right-0 bg-white rounded-full p-1 text-green-500"
                    />
                  </>
                ) : (
                  <div className="bg-red-100 p-3 rounded-lg mb-3">
                    <FontAwesomeIcon
                      icon={faFilePdf}
                      className="text-red-500 text-3xl"
                    />
                  </div>
                )}
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    removeFile(language);
                  }}
                  className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 hover:bg-red-600"
                >
                  <FontAwesomeIcon icon={faTimes} className="text-xs" />
                </button>
              </div>
              <p className="text-sm font-medium text-gray-700 mb-1 truncate max-w-full">
                {file.name}
              </p>
              <p className="text-xs text-gray-500">
                {(file.size / 1024).toFixed(1)} KB
              </p>
            </div>
          ) : hasExistingFile ? (
            <div className="flex flex-col items-center">
              <div className="relative">
                {fileType === "image" ? (
                  <>
                    <img
                      src={resolveMediaUrl(existingFileValue)}
                      alt="Existing"
                      className="h-32 w-32 object-cover rounded-md mb-2 border"
                    />
                    <FontAwesomeIcon
                      icon={faFileImage}
                      className="absolute top-0 right-0 bg-white rounded-full p-1 text-green-500"
                    />
                  </>
                ) : (
                  <div className="bg-green-100 p-3 rounded-lg mb-3">
                    <FontAwesomeIcon
                      icon={faFilePdf}
                      className="text-green-500 text-3xl"
                    />
                  </div>
                )}
              </div>
              <p className="text-sm font-medium text-green-700 mb-2">
                ملف موجود ({fileType === "pdf" ? "PDF" : "صورة"})
              </p>
              <div className="flex space-x-2">
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    viewExistingFile(existingFileValue, language);
                  }}
                  className="text-mauritania-green hover:text-mauritania-green-dark text-sm"
                >
                  <FontAwesomeIcon icon={faEye} className="ml-1" />
                  {getTranslation("preview", currentLang)}
                </button>
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    downloadExistingFile(existingFileValue, fileName, language);
                  }}
                  className="text-blue-600 hover:text-blue-800 text-sm"
                >
                  <FontAwesomeIcon icon={faDownload} className="ml-1" />
                  {getTranslation("download", currentLang)}
                </button>
              </div>
            </div>
          ) : (
            <div>
              <div className="bg-gray-100 p-3 rounded-lg inline-block mb-3">
                <FontAwesomeIcon
                  icon={faUpload}
                  className="text-gray-400 text-2xl"
                />
              </div>
              <p className="text-sm text-gray-600">
                اسحب الملف هنا أو انقر للاختيار
              </p>
              <p className="text-xs text-gray-500 mt-1">PDF, JPG, PNG, GIF</p>
            </div>
          )}
        </div>
        {!file && !hasExistingFile && (
          <p className="text-red-500 text-sm mt-1">هذا الحقل مطلوب</p>
        )}
      </div>
    );
  };

  if (hasExistingRecord && !isEditMode) {
    return (
      <AdminLayout>
        <div className="text-center py-12">
          <div className="bg-yellow-50 border border-yellow-200 rounded-md p-6 max-w-md mx-auto">
            <FontAwesomeIcon
              icon={faInfoCircle}
              className="text-yellow-500 text-4xl mb-4"
            />
            <h2 className="text-xl font-bold text-yellow-700 mb-2">تنبيه</h2>
            <p className="text-yellow-600 mb-4">
              يوجد هيكل إداري بالفعل. يسمح بوجود هيكل إداري واحد فقط في النظام.
            </p>
            <div className="flex justify-center space-x-4">
              <button
                onClick={() =>
                  navigate(`/admin/structure-admin/edit/${existingRecordId}`)
                }
                className="bg-mauritania-green text-white py-2 px-4 rounded hover:bg-mauritania-green-dark transition duration-300"
              >
                تعديل الهيكل الإداري الحالي
              </button>
              <button
                onClick={() => navigate("/admin/structure-admin")}
                className="bg-gray-300 text-gray-700 py-2 px-4 rounded hover:bg-gray-400 transition duration-300"
              >
                العودة للقائمة
              </button>
            </div>
          </div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <style>
        {`
          .rtl-quill .ql-editor {
            direction: rtl;
            text-align: right;
          }
          .rtl-quill .ql-editor ol, 
          .rtl-quill .ql-editor ul {
            padding-right: 1.5em;
            padding-left: 0;
          }
          .ltr-quill .ql-editor {
            direction: ltr;
            text-align: left;
          }
          .ltr-quill .ql-editor ol, 
          .ltr-quill .ql-editor ul {
            padding-left: 1.5em;
            padding-right: 0;
          }
        `}
      </style>

      <div className="flex justify-between items-center mb-8">
        <h1 className="text-3xl font-bold text-mauritania-green-dark">
          {isEditMode
            ? "تعديل الهيكل الإداري"
            : getTranslation("addStructure", currentLang)}
        </h1>
        <button
          onClick={() => navigate("/admin/structure-admin")}
          className="bg-gray-300 text-gray-700 py-2 px-4 rounded hover:bg-gray-400 transition duration-300"
        >
          <FontAwesomeIcon icon={faArrowLeft} className="ml-2" />
          {getTranslation("back", currentLang)}
        </button>
      </div>
      <div className="bg-white rounded-lg shadow-md p-6">
        <form onSubmit={handleSubmit(onSubmit)}>
          {/* Titles */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
            <div>
              <label className="block text-gray-700 mb-2" htmlFor="titrearabe">
                العنوان (عربي) *
              </label>
              <input
                id="titrearabe"
                type="text"
                {...register("titrearabe", { required: "هذا الحقل مطلوب" })}
                className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-mauritania-green ${
                  errors.titrearabe ? "border-red-500" : "border-gray-300"
                }`}
              />
              {errors.titrearabe && (
                <p className="text-red-500 text-sm mt-1">
                  {errors.titrearabe.message}
                </p>
              )}
            </div>

            <div>
              <label
                className="block text-gray-700 mb-2"
                htmlFor="titrefrancais"
              >
                العنوان (فرنسي) *
              </label>
              <input
                id="titrefrancais"
                type="text"
                {...register("titrefrancais", { required: "هذا الحقل مطلوب" })}
                className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-mauritania-green ${
                  errors.titrefrancais ? "border-red-500" : "border-gray-300"
                }`}
              />
              {errors.titrefrancais && (
                <p className="text-red-500 text-sm mt-1">
                  {errors.titrefrancais.message}
                </p>
              )}
            </div>
          </div>

          {/* Text fields (nouveau) */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
            <div>
              <label className="block text-gray-700 mb-2" htmlFor="textarabe">
                النص (عربي)
              </label>
              <div className="rtl-quill">
                <ReactQuill
                  theme="snow"
                  value={arabicText}
                  onChange={setArabicText}
                  modules={quillModules}
                  className="h-64 mb-16"
                />
              </div>
            </div>

            <div>
              <label
                className="block text-gray-700 mb-2"
                htmlFor="textfrancais"
              >
                النص (فرنسي)
              </label>
              <div className="ltr-quill">
                <ReactQuill
                  theme="snow"
                  value={frenchText}
                  onChange={setFrenchText}
                  modules={quillModules}
                  className="h-64 mb-16"
                />
              </div>
            </div>
          </div>

          {/* File Upload Sections */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
            {renderFileUpload(
              "arabic",
              arabicFile,
              arabicFilePreview,
              existingArabicFile,
              arabicDragOver,
              setArabicDragOver,
            )}
            {renderFileUpload(
              "french",
              frenchFile,
              frenchFilePreview,
              existingFrenchFile,
              frenchDragOver,
              setFrenchDragOver,
            )}
          </div>

          {/* Submit */}
          <div className="flex justify-end">
            <button
              type="submit"
              disabled={isLoading}
              className="bg-mauritania-green text-white py-2 px-6 rounded-md hover:bg-mauritania-green-dark transition duration-300 focus:outline-none focus:ring-2 focus:ring-mauritania-green focus:ring-opacity-50 disabled:opacity-50"
            >
              <FontAwesomeIcon icon={faSave} className="ml-2" />
              {isLoading
                ? getTranslation("saving", currentLang)
                : getTranslation("save", currentLang)}
            </button>
          </div>
        </form>
      </div>
    </AdminLayout>
  );
};

export default StructureAdminForm;
